import { Phone, Mail, MapPin, Clock, Globe, Facebook, Instagram, ShieldCheck, Star } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { SUPPORTED_LOCALES } from "../lib/blog-locales";
import logoWebp112 from "../assets/capital-pro-logo-112.webp";
import logoWebp224 from "../assets/capital-pro-logo-224.webp";

// Flip to true once Jordan confirms the Google Business Profile URL.
const SHOW_GOOGLE_PROFILE = false;

// Flip to true once Jordan confirms public social handles.
const SHOW_SOCIAL = true;
const FACEBOOK_URL = "https://www.facebook.com/CapitalProPressureWashing";
const INSTAGRAM_URL = "https://www.instagram.com/capitalpropressurewashing/";

const services = [
  { name: "House Washing", href: "/house-washing" },
  { name: "Roof Washing", href: "/roof-washing" },
  { name: "Roof Moss Removal", href: "/roof-moss-removal" },
  { name: "Soft Washing", href: "/soft-washing" },
  { name: "Paver Cleaning & Sealing", href: "/paver-cleaning-sealing" },
  { name: "Concrete Cleaning & Sealing", href: "/concrete-cleaning-sealing" },
  { name: "Driveway Cleaning", href: "/driveway-cleaning" },
  { name: "Sidewalk & Walkway Cleaning", href: "/sidewalk-cleaning" },
  { name: "Siding Cleaning", href: "/siding-cleaning" },
  { name: "Vinyl Siding Cleaning", href: "/vinyl-siding-cleaning" },
  { name: "Exterior House Cleaning", href: "/exterior-house-cleaning" },
  { name: "Deck & Patio Cleaning", href: "/deck-patio-cleaning" },
  { name: "Patio Cleaning", href: "/patio-cleaning" },
  { name: "Composite & Trex Deck Cleaning", href: "/composite-deck-cleaning" },
  { name: "Rust & Stain Removal", href: "/rust-stain-removal" },
  { name: "Gutter Cleaning", href: "/gutter-cleaning" },
  { name: "Gutter Guards", href: "/gutter-guards" },
  { name: "Window Cleaning", href: "/window-cleaning" },
  { name: "Fence Cleaning", href: "/fence-cleaning" },
  { name: "Holiday Lighting", href: "/holiday-lighting" },
  { name: "Permanent Outdoor Lighting", href: "/permanent-lighting" },
  { name: "Commercial Washing", href: "/commercial-pressure-washing" },
  { name: "Barn & Agricultural Washing", href: "/agricultural-barn-washing" },
];

const areas = [
  { name: "Albany, NY", href: "/pressure-washing-albany-ny" },
  { name: "Schenectady, NY", href: "/pressure-washing-schenectady-ny" },
  { name: "Clifton Park, NY", href: "/pressure-washing-clifton-park-ny" },
  { name: "Saratoga Springs, NY", href: "/pressure-washing-saratoga-springs-ny" },
  { name: "Troy, NY", href: "/pressure-washing-troy-ny" },
  { name: "Rensselaer, NY", href: "/pressure-washing-rensselaer-ny" },
  { name: "Colonie, NY", href: "/pressure-washing-colonie-ny" },
  { name: "Latham, NY", href: "/pressure-washing-latham-ny" },
  { name: "Guilderland, NY", href: "/pressure-washing-guilderland-ny" },
  { name: "Delmar, NY", href: "/pressure-washing-delmar-ny" },
  { name: "Niskayuna, NY", href: "/pressure-washing-niskayuna-ny" },
  { name: "Ballston Spa, NY", href: "/pressure-washing-ballston-spa-ny" },
  { name: "Loudonville, NY", href: "/pressure-washing-loudonville-ny" },
  { name: "Bethlehem, NY", href: "/pressure-washing-bethlehem-ny" },
  { name: "Glenmont, NY", href: "/pressure-washing-glenmont-ny" },
  { name: "East Greenbush, NY", href: "/pressure-washing-east-greenbush-ny" },
  { name: "Halfmoon, NY", href: "/pressure-washing-halfmoon-ny" },
  { name: "Menands, NY", href: "/pressure-washing-menands-ny" },
  { name: "Altamont, NY", href: "/pressure-washing-altamont-ny" },
  { name: "Ravena, NY", href: "/pressure-washing-ravena-ny" },
];


function FooterLogo() {
  return (
    <a href="/" className="inline-flex items-center gap-2" aria-label="Capital Pro Pressure Washing — Home">
      <picture>
        <source type="image/webp" srcSet={`${logoWebp112} 1x, ${logoWebp224} 2x`} />
        <img
          src={logoWebp224}
          alt="Capital Pro Pressure Washing"
          className="h-14 w-auto bg-white rounded-md p-2"
          width={224}
          height={129}
          loading="lazy"
          decoding="async"
        />
      </picture>
    </a>
  );
}

const colHeading =
  "text-white text-base uppercase mb-4";
const colHeadingStyle = {
  fontFamily: "Oswald, sans-serif",
  fontWeight: 700,
  letterSpacing: "0.08em",
} as const;
const linkClass =
  "text-white/75 hover:text-white text-sm transition-colors";

export function SiteFooter() {
  return (
    <footer className="bg-[#1A1A1A] text-white">
      <div className="mx-auto max-w-[1200px] px-4 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Column 1: brand */}
          <div>
            <FooterLogo />
            <p className="text-white/75 text-sm leading-relaxed mt-4">
              Professional exterior cleaning across New York's Capital Region.
              Veteran owned &amp; operated, family run, 10 years in business.
            </p>
            <div className="mt-5 flex flex-col items-start gap-2">
              <span
                className="inline-flex items-center gap-2 rounded-md border border-white/25 bg-white/5 px-3 py-2 text-white text-xs uppercase tracking-widest"
                style={{ fontFamily: "Oswald, sans-serif", fontWeight: 700, letterSpacing: "0.12em" }}
              >
                <Star size={16} strokeWidth={2.5} className="text-[#C8102E]" fill="#C8102E" />
                Veteran Owned &amp; Operated &middot; US Army
              </span>
              <span
                className="inline-flex items-center gap-2 rounded-md border border-[#C8102E]/70 bg-[#C8102E]/10 px-3 py-2 text-white text-xs uppercase tracking-widest"
                style={{ fontFamily: "Oswald, sans-serif", fontWeight: 700, letterSpacing: "0.12em" }}
              >
                <ShieldCheck size={16} strokeWidth={2.5} className="text-[#C8102E]" />
                Workers Comp &amp; Liability Insured
              </span>
              <span
                className="inline-flex items-center gap-2 rounded-md border border-white/25 bg-white/5 px-3 py-2 text-white text-xs uppercase tracking-widest"
                style={{ fontFamily: "Oswald, sans-serif", fontWeight: 700, letterSpacing: "0.12em" }}
              >
                <ShieldCheck size={16} strokeWidth={2.5} className="text-[#C8102E]" />
                Google Guaranteed
              </span>
              <a
                href="https://business.bethlehemchamber.com/list/member/capital-pro-pressure-washing-5723"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-white/25 bg-white/5 px-3 py-2 text-white text-xs uppercase tracking-widest hover:border-white/50 transition-colors"
                style={{ fontFamily: "Oswald, sans-serif", fontWeight: 700, letterSpacing: "0.12em" }}
              >
                <Star size={16} strokeWidth={2.5} className="text-[#C8102E]" />
                Bethlehem Chamber Member
              </a>
            </div>
          </div>

          {/* Column 2: services */}
          <div>
            <h3 className={colHeading} style={colHeadingStyle}>Services</h3>
            <ul className="space-y-2">
              <li>
                <a href="/services" className="text-white font-semibold text-sm transition-colors hover:text-[#C8102E]">All Services</a>
              </li>
              {services.map((s) => (
                <li key={s.name}>
                  <a href={s.href} className={linkClass}>{s.name}</a>
                </li>
              ))}
              <li>
                <a href="/financing" className={linkClass}>Financing Available</a>
              </li>
              <li>
                <a href="/annual-maintenance" className={linkClass}>Home Care Memberships</a>
              </li>
              <li>
                <a href="/concrete-sealing-freeze-thaw" className={linkClass}>Concrete Freeze Protection</a>
              </li>
              <li>
                <a href="/paver-sand-seal" className={linkClass}>Paver Sand &amp; Seal</a>
              </li>
              <li>
                <a href="/hardscape-maintenance" className={linkClass}>Hardscape Maintenance Plans</a>
              </li>
              <li>
                <a href="/pricing" className={linkClass}>Pricing Guide</a>
              </li>
              <li>
                <a href="/blog" className={linkClass}>Blog &amp; Cleaning Tips</a>
              </li>

            </ul>
          </div>

          {/* Column 3: service area */}
          <div>
            <h3 className={colHeading} style={colHeadingStyle}>Service Area</h3>
            <ul className="space-y-2">
              {areas.map((a) => (
                <li key={a.name} className="flex items-start gap-2">
                  <MapPin size={14} className="text-[#C8102E] shrink-0 mt-1" strokeWidth={2} />
                  <a href={a.href} className={linkClass}>{a.name}</a>
                </li>
              ))}
              <li className="pt-2 border-t border-white/15 mt-2">
                <a href="/service-area" className="text-[#C8102E] hover:text-white text-sm font-bold uppercase tracking-wide transition-colors">View All Areas</a>
              </li>
            </ul>
          </div>

          {/* Column 4: contact */}
          <div>
            <h3 className={colHeading} style={colHeadingStyle}>Contact</h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="tel:+15189001913"
                  className="inline-flex items-center gap-2 text-white hover:text-[#C8102E] text-sm font-bold transition-colors"
                >
                  <Phone size={16} strokeWidth={2.5} />
                  (518) 900-1913
                </a>
              </li>
              <li>
                <a
                  href="mailto:luist@capitalpropw.com"
                  className="inline-flex items-center gap-2 text-white hover:text-[#C8102E] text-sm transition-colors"
                >
                  <Mail size={16} strokeWidth={2} />
                  luist@capitalpropw.com
                </a>
              </li>
              <li>
                <span className="inline-flex items-start gap-2 text-white/75 text-sm">
                  <MapPin size={16} strokeWidth={2} className="shrink-0 mt-0.5" />
                  <span>
                    5A Vatrano Rd, Albany, NY 12205
                    <span className="block text-white/60 text-xs mt-0.5">
                      Mobile service — we come to you across the Capital Region.
                    </span>
                  </span>
                </span>
              </li>
              <li>
                <span className="inline-flex items-start gap-2 text-white/75 text-sm">
                  <Clock size={16} strokeWidth={2} />
                  Mon–Fri, 9am–5pm ET
                </span>
              </li>
              <li>
                <a href="/about" className="text-white/75 hover:text-white text-sm transition-colors">About Capital Pro</a>
              </li>
              <li>
                <a href="/contact" className="text-white/75 hover:text-white text-sm transition-colors">Contact Us</a>
              </li>
              <li>
                <a href="/pressure-washing-near-me" className="text-white/75 hover:text-white text-sm transition-colors">Pressure Washing Near Me</a>
              </li>
              <li>
                <a href="/guides" className="text-white/75 hover:text-white text-sm transition-colors">Cleaning Guides</a>
              </li>
              <li>
                <a href="/gallery" className="text-white/75 hover:text-white text-sm transition-colors">Before &amp; After Gallery</a>
              </li>
              <li>
                <a href="/reviews" className="text-white/75 hover:text-white text-sm transition-colors">Customer Reviews</a>
              </li>
              {SHOW_GOOGLE_PROFILE && (
                <li>
                  <a
                    href="#"
                    data-placeholder="CONFIRM_GOOGLE_BUSINESS_PROFILE_URL"
                    className="inline-flex items-center gap-2 text-white/75 hover:text-white text-sm transition-colors"
                  >
                    <Globe size={16} strokeWidth={2} />
                    Google Business Profile
                  </a>
                </li>
              )}
              {SHOW_SOCIAL && (
                <li className="flex items-center gap-3 pt-1">
                  <a
                    href={FACEBOOK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    className="text-white/75 hover:text-white transition-colors"
                  >
                    <Facebook size={20} strokeWidth={2} />
                  </a>
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    className="text-white/75 hover:text-white transition-colors"
                  >
                    <Instagram size={20} strokeWidth={2} />
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-[1200px] px-4 pt-5 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-white/60">
          <span className="font-medium text-white/70">Blog:</span>
          {SUPPORTED_LOCALES.map((l) => (
            <Link
              key={l.code}
              to={l.indexRoute as "/blog" | "/es/blog" | "/zh/blog" | "/ur/blog" | "/ar/blog"}
              hrefLang={l.hrefLang}
              lang={l.htmlLang}
              dir={l.dir}
              className="text-white/70 hover:text-white underline underline-offset-2 transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>
        <div className="mx-auto max-w-[1200px] px-4 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/60">
          <p>© 2026 Capital Pro Pressure Washing. All rights reserved.</p>
          <p>
            Site by{" "}
            <a
              href="https://washerauthority.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 hover:text-white underline underline-offset-2 transition-colors"
            >
              Washer Authority
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}